import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import api from 'app/utils/axiosInstance';
import colors, { useColors } from 'app/utils/colors';
import { setUserData } from 'app/redux/auth/authSlice';
import localStorageService from 'app/services/localStorageService';

const getToken = () => localStorage.getItem('access_token');

function ShieldCheckIcon() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#10B981"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="#DCFCE7" />
      <path d="M9 12l2 2 4-4" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function TwoFactorSetup({ show, onClose, initialStep }) {
  const dynamicColors = useColors();
  const { t } = useTranslation('settings');
  const dispatch = useDispatch();
  const [step, setStep] = useState('idle');
  const [qrCode, setQrCode] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const twofaEnabled = user?.twoFactorEnabled;

  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [confirmedSaved, setConfirmedSaved] = useState(false);
  const [hasExistingSecret, setHasExistingSecret] = useState(false);

  const resetAndClose = () => {
    if (step === 'qr' && twofaEnabled) {
      api.post('/auth/cancel-2fa-reset', {}, { headers: { Authorization: `Bearer ${getToken()}` } }).catch(() => { });
    }
    setStep('idle');
    setQrCode('');
    setToken('');
    setError('');
    setLoading(false);
    setRecoveryCodes([]);
    setConfirmedSaved(false);
    onClose?.();
  };

  const handleEnable = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post(
        '/auth/enable-2fa',
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setQrCode(data.qrCodeDataUrl);
      setStep('qr');
    } catch {
      setError(t('twoFactorAuth.errors.qrFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      if (initialStep === 'reset') {
        handleEnable();
      } else if (!twofaEnabled) {
        setLoading(true);
        api
          .get('/auth/2fa/status', { headers: { Authorization: `Bearer ${getToken()}` } })
          .then(({ data }) => {
            const hasSecret = Boolean(data?.hasExistingSecret);
            setHasExistingSecret(hasSecret);
            if (hasSecret) {
              setStep('choice');
            } else {
              handleEnable();
            }
          })
          .catch(() => {
            handleEnable();
          })
          .finally(() => setLoading(false));
      }
    }
  }, [show, initialStep, twofaEnabled]);

  const updateReduxAndStorage = (twoFactorEnabledStatus) => {
    const updatedUser = { ...user, twoFactorEnabled: twoFactorEnabledStatus };
    dispatch(setUserData({ user: updatedUser }));
    const stored = localStorageService.getItem('auth_user');
    if (stored) {
      stored.user = updatedUser;
      localStorageService.setItem('auth_user', stored);
    }
  };

  const handleVerify = async (options = {}) => {
    const { reuseExisting = false } = options;
    if (token.length !== 6) {
      const errText = t('twoFactorAuth.errors.codeRequired');
      setError(errText);
      toast.error(errText);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post(
        '/auth/verify-2fa',
        { token, reuseExisting },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      updateReduxAndStorage(true);
      if (data.recoveryCodes && data.recoveryCodes.length > 0) {
        setRecoveryCodes(data.recoveryCodes);
        setStep('showRecovery');
      } else {
        toast.success(t('twoFactorAuth.done.title'));
        setStep('done');
      }
    } catch (err) {
      const msg = err?.response?.data?.message;
      const displayMsg = Array.isArray(msg) ? msg.join(', ') : (msg || t('twoFactorAuth.errors.invalidCode'));
      setError(displayMsg);
      toast.error(displayMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post(
        '/auth/2fa/recovery/regenerate',
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      if (data.recoveryCodes && data.recoveryCodes.length > 0) {
        setRecoveryCodes(data.recoveryCodes);
        setStep('showRecovery');
        toast.success(t('twoFactorAuth.recovery.regenerateTitle') || 'Recovery codes regenerated');
      }
    } catch (err) {
      const msg = err?.response?.data?.message;
      const displayMsg = Array.isArray(msg) ? msg.join(', ') : (msg || 'Failed to regenerate recovery codes');
      setError(displayMsg);
      toast.error(displayMsg);
    } finally {
      setLoading(false);
    }
  };

  const copyRecoveryCodes = () => {
    if (!recoveryCodes.length) return;
    const text = recoveryCodes.join('\n');
    navigator.clipboard.writeText(text);
    toast.success(t('twoFactorAuth.recovery.copied') || 'Codes copied to clipboard!');
  };

  const downloadRecoveryCodes = () => {
    if (!recoveryCodes.length) return;
    const text = `Billswift 2FA Recovery Codes\nGenerated at: ${new Date().toLocaleString()}\n\nKeep these codes safe. Each code can be used only once.\n\n${recoveryCodes.join('\n')}\n`;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'billswift-2fa-recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDisable = async () => {
    if (token.length !== 6) {
      const errText = t('twoFactorAuth.errors.codeRequired');
      setError(errText);
      toast.error(errText);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post(
        '/auth/disable-2fa',
        { token },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      updateReduxAndStorage(false);
      toast.success(t('twoFactorAuth.disable.success'));
      resetAndClose();
    } catch (err) {
      const msg = err?.response?.data?.message;
      const displayMsg = Array.isArray(msg) ? msg.join(', ') : (msg || t('twoFactorAuth.errors.invalidCode'));
      setError(displayMsg);
      toast.error(displayMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={styles.overlay} onClick={resetAndClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={resetAndClose}
          style={styles.closeBtn}
        >
          ×
        </button>

        {/* ── Choice Screen (When re-enabling and user has existing secret) ── */}
        {step === 'choice' ? (
          <>
            <div style={styles.header}>
              <div style={styles.shieldCircle}>
                <ShieldCheckIcon />
              </div>
              <h2 style={styles.title}>{t('twoFactorAuth.reEnable.title')}</h2>
              <p style={styles.subtitle}>{t('twoFactorAuth.reEnable.choiceSubtitle')}</p>
            </div>
            <div style={styles.separator} />

            <div style={styles.scrollArea}>
              <div style={styles.body}>
                {/* Option A: Use existing authenticator */}
                <div
                  role="button"
                  tabIndex={0}
                  style={styles.optionCard}
                  onClick={() => { setStep('reuseCode'); setToken(''); setError(''); }}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (setStep('reuseCode'), setToken(''), setError(''))}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                    <span style={{ fontSize: '26px', lineHeight: 1 }}>📱</span>
                    <div>
                      <h4 style={styles.optionTitle}>{t('twoFactorAuth.reEnable.optionA.title')}</h4>
                      <p style={styles.optionDesc}>{t('twoFactorAuth.reEnable.optionA.description')}</p>
                    </div>
                  </div>
                </div>

                {/* Option B: Set up again with a new QR code */}
                <div
                  role="button"
                  tabIndex={0}
                  style={styles.optionCard}
                  onClick={() => handleEnable()}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleEnable()}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                    <span style={{ fontSize: '26px', lineHeight: 1 }}>📷</span>
                    <div>
                      <h4 style={styles.optionTitle}>{t('twoFactorAuth.reEnable.optionB.title')}</h4>
                      <p style={styles.optionDesc}>{t('twoFactorAuth.reEnable.optionB.description')}</p>
                      <div style={styles.warningNote}>
                        ⚠️ {t('twoFactorAuth.reEnable.optionB.warningNote')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {error && <p style={styles.error}>{error}</p>}
            </div>
          </>
        ) : step === 'reuseCode' ? (
          /* ── Option A: Enter code from existing authenticator (No QR code) ── */
          <>
            <div style={styles.header}>
              <div style={styles.shieldCircle}>
                <ShieldCheckIcon />
              </div>
              <h2 style={styles.title}>{t('twoFactorAuth.reEnable.reuseCode.title')}</h2>
              <p style={styles.subtitle}>{t('twoFactorAuth.reEnable.reuseCode.subtitle')}</p>
            </div>
            <div style={styles.separator} />

            <div style={styles.scrollArea}>
              <div style={styles.body}>
                <p style={styles.label}>{t('twoFactorAuth.reEnable.reuseCode.codeLabel')}</p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={t('twoFactorAuth.reEnable.reuseCode.codePlaceholder')}
                  value={token}
                  onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
                  style={styles.input}
                />
                <button
                  style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
                  onClick={() => handleVerify({ reuseExisting: true })}
                  disabled={loading}
                >
                  {loading
                    ? t('twoFactorAuth.reEnable.reuseCode.confirming')
                    : t('twoFactorAuth.reEnable.reuseCode.confirm')}
                </button>
                <button
                  style={{ ...styles.secondaryBtn, marginTop: '10px' }}
                  onClick={() => { setStep('choice'); setToken(''); setError(''); }}
                  disabled={loading}
                >
                  ← {t('common.cancel')}
                </button>
              </div>
              {error && <p style={styles.error}>{error}</p>}
            </div>
          </>
        ) : step === 'qr' ? (
          /* ── Option B / First-time: QR Code Screen ── */
          <>
            <div style={styles.header}>
              <div style={styles.shieldCircle}>
                <ShieldCheckIcon />
              </div>
              <h2 style={styles.title}>
                {twofaEnabled ? t('twoFactorAuth.reset.title') : t('twoFactorAuth.title')}
              </h2>
            </div>
            <div style={styles.separator} />

            <div style={styles.scrollArea}>
              <div style={styles.body}>
                <div style={styles.middleSection}>
                  <ol style={styles.steps}>
                    <li>{t('twoFactorAuth.qr.step1')}</li>
                    <li>{t('twoFactorAuth.qr.step2')}</li>
                    <li>{t('twoFactorAuth.qr.step3')}</li>
                  </ol>

                  <div style={styles.qrWrapper}>
                    <img src={qrCode} alt="" style={styles.qr} />
                  </div>

                  <p style={styles.label}>{t('twoFactorAuth.qr.codeLabel')}</p>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder={t('twoFactorAuth.qr.codePlaceholder')}
                    value={token}
                    onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
                    style={styles.input}
                  />
                </div>

                <button
                  style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
                  onClick={() => handleVerify({ reuseExisting: false })}
                  disabled={loading}
                >
                  {loading
                    ? t('twoFactorAuth.qr.confirming')
                    : twofaEnabled
                      ? t('twoFactorAuth.reset.confirmBtn')
                      : t('twoFactorAuth.qr.confirm')}
                </button>

                {hasExistingSecret && !twofaEnabled && (
                  <button
                    style={{ ...styles.secondaryBtn, marginTop: '10px' }}
                    onClick={() => { setStep('choice'); setToken(''); setError(''); }}
                    disabled={loading}
                  >
                    ← {t('common.cancel')}
                  </button>
                )}
              </div>
              {error && <p style={styles.error}>{error}</p>}
            </div>
          </>
        ) : step === 'showRecovery' ? (
          /* ── Recovery Codes Screen ── */
          <div style={{ padding: '28px 32px 32px' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 6px', color: '#111827' }}>
                {t('twoFactorAuth.recovery.title')}
              </h2>
              <p style={{ fontSize: 13, color: '#6B7280', margin: 0, lineHeight: 1.5 }}>
                {t('twoFactorAuth.recovery.subtitle')}
              </p>
            </div>

            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
              padding: '10px 14px', fontSize: 12.5, color: '#DC2626', marginBottom: 16, lineHeight: 1.45
            }}>
              ⚠️ {t('twoFactorAuth.recovery.warning')}
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px',
              background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10,
              padding: '14px 18px', marginBottom: 16
            }}>
              {recoveryCodes.map((code, idx) => (
                <div key={idx} style={{
                  fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: '#1F2937',
                  letterSpacing: '1px', textAlign: 'center', padding: '6px 8px',
                  background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 6
                }}>
                  {code}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <button
                type="button"
                onClick={copyRecoveryCodes}
                style={{
                  flex: 1, padding: '10px', background: '#F3F4F6', color: '#374151',
                  border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer'
                }}
              >
                📋 {t('twoFactorAuth.recovery.copyAll')}
              </button>
              <button
                type="button"
                onClick={downloadRecoveryCodes}
                style={{
                  flex: 1, padding: '10px', background: '#F3F4F6', color: '#374151',
                  border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer'
                }}
              >
                📥 {t('twoFactorAuth.recovery.download')}
              </button>
            </div>

            <label style={{
              display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#374151',
              marginBottom: 20, cursor: 'pointer', userSelect: 'none'
            }}>
              <input
                type="checkbox"
                checked={confirmedSaved}
                onChange={(e) => setConfirmedSaved(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: dynamicColors.primary || 'var(--theme-primary, #f95f1f)', cursor: 'pointer' }}
              />
              <span>{t('twoFactorAuth.recovery.savedConfirm')}</span>
            </label>

            <button
              style={{
                ...styles.btn,
                ...(!confirmedSaved ? styles.btnDisabled : {})
              }}
              onClick={resetAndClose}
              disabled={!confirmedSaved}
            >
              {t('twoFactorAuth.recovery.doneBtn')}
            </button>
          </div>
        ) : twofaEnabled ? (
          /* ── 2FA Configured Views ── */
          step === 'disableConfirm' ? (
            <>
              <div style={styles.header}>
                <div style={styles.shieldCircle}>
                  <ShieldCheckIcon />
                </div>
                <h2 style={styles.title}>{t('twoFactorAuth.disable.title')}</h2>
                <p style={styles.subtitle}>{t('twoFactorAuth.disable.warning')}</p>
              </div>
              <div style={styles.separator} />
              <div style={styles.scrollArea}>
                <div style={styles.body}>
                  <p style={styles.label}>{t('twoFactorAuth.disable.codeLabel')}</p>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder={t('twoFactorAuth.disable.codePlaceholder')}
                    value={token}
                    onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
                    style={styles.input}
                  />
                  <button
                    style={{ ...styles.dangerBtn, ...(loading ? styles.btnDisabled : {}) }}
                    onClick={handleDisable}
                    disabled={loading}
                  >
                    {loading ? t('twoFactorAuth.disable.confirming') : t('twoFactorAuth.disable.confirm')}
                  </button>
                  <button
                    style={styles.secondaryBtn}
                    onClick={() => { setStep('idle'); setToken(''); setError(''); }}
                    disabled={loading}
                  >
                    {t('twoFactorAuth.disable.cancel')}
                  </button>
                </div>
                {error && <p style={styles.error}>{error}</p>}
              </div>
            </>
          ) : step === 'regenerateConfirm' ? (
            <>
              <div style={styles.header}>
                <div style={styles.shieldCircle}>
                  <ShieldCheckIcon />
                </div>
                <h2 style={styles.title}>{t('twoFactorAuth.recovery.regenerateTitle')}</h2>
                <p style={styles.subtitle}>{t('twoFactorAuth.recovery.regenerateSubtitle')}</p>
              </div>
              <div style={styles.separator} />
              <div style={styles.scrollArea}>
                <div style={styles.body}>
                  <button
                    style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
                    onClick={handleRegenerate}
                    disabled={loading}
                  >
                    {loading ? t('twoFactorAuth.recovery.regenerating') : t('twoFactorAuth.recovery.regenerateBtn')}
                  </button>
                  <button
                    style={{ ...styles.secondaryBtn, marginTop: 10 }}
                    onClick={() => setStep('idle')}
                    disabled={loading}
                  >
                    {t('twoFactorAuth.disable.cancel')}
                  </button>
                </div>
                {error && <p style={styles.error}>{error}</p>}
              </div>
            </>
          ) : (
            <div style={styles.configuredBody}>
              <div style={styles.shieldCircle}>
                <ShieldCheckIcon />
              </div>
              <h2 style={styles.configuredTitle}>
                {t('twoFactorAuth.configured.title')}
              </h2>
              <p style={styles.configuredSubtitle}>
                {t('twoFactorAuth.configured.subtitle')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 10 }}>
                <button
                  style={styles.btn}
                  onClick={handleEnable}
                >
                  📱 {t('twoFactorAuth.reset.btn')}
                </button>
                <button
                  style={styles.secondaryBtn}
                  onClick={() => setStep('regenerateConfirm')}
                >
                  🔄 {t('twoFactorAuth.recovery.regenerateTitle')}
                </button>
                <button
                  style={styles.disableOutlineBtn}
                  onClick={() => { setStep('disableConfirm'); setToken(''); setError(''); }}
                >
                  {t('twoFactorAuth.configured.disableBtn')}
                </button>
              </div>
            </div>
          )
        ) : (
          /* ── Initial Unconfigured 2FA Step: idle ── */
          <>
            <div style={styles.header}>
              <div style={styles.shieldCircle}>
                <ShieldCheckIcon />
              </div>
              <h2 style={styles.title}>{t('twoFactorAuth.title')}</h2>
              <p style={styles.subtitle}>{t('twoFactorAuth.subtitle')}</p>
            </div>
            <div style={styles.separator} />
            <div style={styles.scrollArea}>
              <div style={styles.body}>
                <p style={styles.description}>{t('twoFactorAuth.idle.description')}</p>
                <button
                  style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
                  onClick={handleEnable}
                  disabled={loading}
                >
                  {loading ? t('twoFactorAuth.idle.enabling') : t('twoFactorAuth.idle.enable')}
                </button>
              </div>
              {error && <p style={styles.error}>{error}</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
    padding: '24px',
    boxSizing: 'border-box',
  },
  card: {
    position: 'relative',
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 20px 48px rgba(0,0,0,0.16)',
    width: '100%',
    maxWidth: '460px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  closeBtn: {
    position: 'absolute',
    top: 12,
    right: 14,
    background: 'transparent',
    border: 'none',
    fontSize: '22px',
    lineHeight: 1,
    color: '#9ca3af',
    cursor: 'pointer',
    padding: 4,
    zIndex: 1,
  },
  header: {
    background: 'white',
    padding: '24px 28px 12px',
    textAlign: 'center',
    flexShrink: 0,
  },
  shieldCircle: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: '#E6F4EA',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto 10px',
  },
  configuredBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 28px 36px',
    textAlign: 'center',
  },
  configuredTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 8px',
    lineHeight: '1.3',
  },
  configuredSubtitle: {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.5',
    margin: '0 0 24px',
    maxWidth: '340px',
  },
  disableOutlineBtn: {
    width: '100%',
    padding: '11px',
    background: '#ffffff',
    color: '#ef4444',
    border: '1px solid #fca5a5',
    borderRadius: '10px',
    fontSize: '14.5px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  dangerBtn: {
    width: '100%',
    padding: '12px',
    background: '#dc2626',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
    marginBottom: '10px',
  },
  secondaryBtn: {
    width: '100%',
    padding: '11px',
    background: 'transparent',
    color: '#6b7280',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  optionCard: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    textAlign: 'left',
  },
  optionTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 4px',
  },
  optionDesc: {
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: '1.45',
    margin: 0,
  },
  warningNote: {
    marginTop: '10px',
    padding: '8px 10px',
    background: '#FFFBEB',
    border: '1px solid #FDE68A',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#92400E',
    lineHeight: '1.4',
  },
  title: {
    margin: '0 0 4px',
    fontSize: '20px',
    fontWeight: '700',
    color: '#1a1a2e',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    margin: 0,
    fontSize: '13.5px',
    color: '#6b7280',
    lineHeight: '1.5',
  },
  separator: {
    height: '1px',
    backgroundColor: '#E5E7EB',
    margin: '0 28px',
    flexShrink: 0,
  },
  scrollArea: {
    overflowY: 'visible',
  },
  body: {
    padding: '16px 28px 26px',
  },
  middleSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginTop: '14px',
    marginBottom: '24px',
  },
  description: {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.55',
    marginBottom: '20px',
  },
  steps: {
    fontSize: '13.5px',
    color: '#374151',
    lineHeight: '1.6',
    paddingLeft: '18px',
    textAlign: 'left',
    display: 'inline-block',
    margin: '0 auto 16px',
    maxWidth: '380px',
  },
  qrWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '0 auto 16px',
    padding: '8px',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    background: '#ffffff',
    width: 'fit-content',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  qr: {
    width: '135px',
    height: '135px',
    display: 'block',
  },
  label: {
    fontSize: '13.5px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
    textAlign: 'center',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '22px',
    fontWeight: '700',
    letterSpacing: '6px',
    textAlign: 'center',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    outline: 'none',
    marginBottom: '14px',
    boxSizing: 'border-box',
    color: '#1a1a2e',
    height: '46px',
    transition: 'border-color 0.2s',
  },
  btn: {
    width: '100%',
    padding: '12px',
    background: 'var(--theme-primary, #f95f1f)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s, opacity 0.2s',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    margin: '0 24px 16px',
    padding: '8px 12px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    fontSize: '12.5px',
    color: '#dc2626',
  },
  doneBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '48px 24px',
    textAlign: 'center',
  },
  doneIconCircle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '12px',
  },
  doneCheckImg: {
    width: 56,
    height: 56,
  },
  doneTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 6px',
  },
  doneSubtitle: {
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: '1.5',
    margin: 0,
  },
};